/**
 * QL-314 — Volatility Chart Widget.
 *
 * Self-contained: fetches its own data, renders the volatility data with
 * ceiling and baseline, and handles the accept/reject deviation UI.
 * Used on the advisor's goal page.
 */

import { pickT } from "@repo/shared";
import type { VolatilityChartPoint, VolatilityDecisionValue } from "@repo/shared/types";
import {
  ASSET_CLASS_FORECAST_HORIZONS,
  type AssetClassForecastHorizon,
  selectAssetClassForecastPoints,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChartLegendStrip } from "@/components/ui/chart-legend-strip";
import { ComplianceSummaryCard } from "@/components/ui/compliance-summary-card";
import { DeviationDecisionCard } from "@/components/ui/deviation-decision-card";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  getVolatilityChartState,
  saveVolatilityChartDecision,
} from "@/lib/api/flow-instances/volatility-chart";
import { formatForecastGridDate } from "@/lib/date-utils";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import { flowCompletionQueryKey } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { T } from "./volatility-chart-widget.i18n";

/** The line's colours, shared by the chart and the legend strip's swatch. */
const LINE_COLOR = { light: "#2563eb", dark: "#60a5fa" } as const;
const BASELINE_COLOR = { light: "#6b7280", dark: "#9ca3af" } as const;

// ── Vega-Lite line chart ──────────────────────────────────────────────────────

function buildVolatilityChartSpec(
  points: VolatilityChartPoint[],
  maxVolatilityPct: number | null,
  baselineVolatilityPct: number | null,
  isDark: boolean,
  width: number,
  language: string,
) {
  const t = pickT(T, language);
  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const lineColor = isDark ? LINE_COLOR.dark : LINE_COLOR.light;
  const ceilingColor = "#dc2626";
  const baselineColor = isDark ? BASELINE_COLOR.dark : BASELINE_COLOR.light;

  const drawn = points.filter((p) => p.kind !== "baseline" && p.kind !== "anniversary");
  const data = drawn.map((p) => ({
    label: p.label,
    volatility: Math.round(p.volatilityPct * 100) / 100,
  }));

  const allValues = data.map((d) => d.volatility);
  if (maxVolatilityPct != null) allValues.push(maxVolatilityPct);
  if (baselineVolatilityPct != null) allValues.push(baselineVolatilityPct);
  const rawMax = allValues.length > 0 ? Math.max(...allValues) : 10;
  const yMax = Math.ceil((rawMax * 1.15) / 2) * 2;

  const layers: any[] = [
    {
      data: { values: data },
      mark: {
        type: "line",
        point: { size: 40, filled: true },
        strokeWidth: 2.5,
        interpolate: "monotone",
        color: lineColor,
      },
      encoding: {
        x: {
          field: "label",
          type: "nominal",
          sort: null,
          axis: {
            title: null,
            labelColor: textColor,
            tickColor: textColor,
            grid: false,
            domain: false,
            labelAngle: -30,
            labelFontSize: 11,
          },
        },
        y: {
          field: "volatility",
          type: "quantitative",
          scale: { domainMin: 0, domainMax: yMax },
          axis: {
            title: `${t.volatility} (%)`,
            titleColor: textColor,
            labelColor: textColor,
            tickColor: textColor,
            gridColor,
            domain: false,
            format: ".1f",
            labelFontSize: 11,
          },
        },
        tooltip: [
          { field: "label", type: "nominal", title: t.date },
          {
            field: "volatility",
            type: "quantitative",
            title: `${t.volatility} (%)`,
            format: ".2f",
          },
        ],
      },
    },
  ];

  // The ceiling and baseline values are revealed on hover, never printed on
  // the chart (DESIGN.md); the legend strip under the chart carries them.
  if (maxVolatilityPct != null) {
    layers.push({
      data: { values: [{ label: `${t.ceilingLabel}: ${maxVolatilityPct}%` }] },
      mark: { type: "rule", strokeDash: [6, 3], color: ceilingColor, strokeWidth: 1.5 },
      encoding: {
        y: { datum: maxVolatilityPct },
        tooltip: { field: "label", type: "nominal" },
      },
    });
  }

  if (baselineVolatilityPct != null) {
    layers.push({
      data: { values: [{ label: `${t.baselineLabel}: ${baselineVolatilityPct.toFixed(1)}%` }] },
      mark: { type: "rule", strokeDash: [4, 4], color: baselineColor, strokeWidth: 1 },
      encoding: {
        y: { datum: baselineVolatilityPct },
        tooltip: { field: "label", type: "nominal" },
      },
    });
  }

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height: 260,
    background: "transparent",
    layer: layers,
    config: { view: { stroke: "transparent" } },
  };
}

function VolatilityLineChart({
  points,
  maxVolatilityPct,
  baselineVolatilityPct,
  language,
}: {
  points: VolatilityChartPoint[];
  maxVolatilityPct: number | null;
  baselineVolatilityPct: number | null;
  language: string;
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";
  const effectiveWidth = containerWidth ?? 500;

  const spec = useMemo(() => {
    if (points.length === 0 || !containerWidth) return null;
    const raw = buildVolatilityChartSpec(
      points,
      maxVolatilityPct,
      baselineVolatilityPct,
      isDark,
      effectiveWidth,
      language,
    );
    return JSON.parse(JSON.stringify(raw));
  }, [
    points,
    maxVolatilityPct,
    baselineVolatilityPct,
    isDark,
    effectiveWidth,
    language,
    containerWidth,
  ]);

  const t = pickT(T, language);
  const drawn = points.filter((p) => p.kind !== "baseline" && p.kind !== "anniversary");
  if (drawn.length === 0) {
    return <div className="p-4 text-muted-foreground text-sm">{t.noDataPoints}</div>;
  }

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: 280 }}
    >
      {spec && <VegaEmbed spec={spec} options={{ actions: false, renderer: "svg" }} />}
    </div>
  );
}

// ── Widget ───────────────────────────────────────────────────────────────────

type VolatilityChartWidgetProps = {
  instanceId: string;
  goalId: string;
  config?: {
    style?: string;
    title?: string;
    description?: string;
    enabledHorizons?: AssetClassForecastHorizon[];
    defaultHorizon?: string;
    showTable?: boolean;
  };
  language: string;
  hasValidationError?: boolean;
  /** True on a locked (submitted / completed) instance: the decision is shown, never edited. */
  disabled?: boolean;
};

export function VolatilityChartWidget({
  instanceId,
  goalId,
  config,
  language,
  hasValidationError,
  disabled = false,
}: VolatilityChartWidgetProps) {
  const t = pickT(T, language);
  const qc = useQueryClient();
  const { resolvedTheme } = useTheme();
  const stripDark = resolvedTheme === "dark";

  const {
    data: state,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: goalSnapshotQueryKeys.volatilityChart(goalId),
    queryFn: () => getVolatilityChartState(instanceId, goalId),
  });

  const enabledHorizons: AssetClassForecastHorizon[] = config?.enabledHorizons?.length
    ? config.enabledHorizons
    : [...ASSET_CLASS_FORECAST_HORIZONS];
  const defaultHorizon = (config?.defaultHorizon ??
    enabledHorizons[0] ??
    "1Y") as AssetClassForecastHorizon;
  const [horizon, setHorizon] = useState<AssetClassForecastHorizon>(defaultHorizon);

  // Decision state — synced from server, editable locally until Save
  const [decision, setDecision] = useState<VolatilityDecisionValue | "">("");
  const [justification, setJustification] = useState("");
  useEffect(() => {
    setDecision(state?.decision?.decision ?? "");
    setJustification(state?.decision?.justification ?? "");
  }, [state?.decision?.decision, state?.decision?.justification]);

  const saveMutation = useMutation({
    mutationFn: (args: { decision: VolatilityDecisionValue | null; justification?: string }) =>
      saveVolatilityChartDecision(instanceId, goalId, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.volatilityChart(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
    },
  });

  const handleSave = async (value: VolatilityDecisionValue | null) => {
    try {
      const result = await saveMutation.mutateAsync({
        decision: value,
        ...(value === "accepted" ? { justification } : {}),
      });
      if (!result.success) {
        toast.error(t.error, { description: result.error });
        return;
      }
      toast.success(t.decisionSaved);
    } catch (err) {
      toast.error(t.error, { description: err instanceof Error ? err.message : undefined });
    }
  };

  const visiblePoints = useMemo(
    // Shared horizon rule (the PDF block draws the same selection for the range
    // its designer picked): the anchor year's end plus the year-ends, so the
    // LINE starts in the first year.
    () => (state?.points ? selectAssetClassForecastPoints(state.points, horizon) : []),
    [state?.points, horizon],
  );
  // The year-1 chip says which date the figure is read at on hover: the
  // anniversary point is drawn nowhere.
  const year1Date = state?.points.find((p) => p.kind === "anniversary")?.date ?? null;
  const year1Hint = year1Date
    ? t.year1Hint(formatForecastGridDate(year1Date, language))
    : undefined;

  const maxUnknownPct = useMemo(() => {
    if (!visiblePoints.length) return 0;
    return Math.max(...visiblePoints.map((p) => p.unknownPct));
  }, [visiblePoints]);

  // Empty / error states follow the sibling snapshot widgets (asset allocation,
  // portfolio forecast): a dashed placeholder for "nothing to show yet", a
  // red-bordered block for a failure. The configured style wraps the chart
  // only, as it does for them.
  if (isLoading) return null;

  if (isError) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 flex items-center gap-2 text-sm text-destructive">
        {t.fetchError}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => refetch()}
        >
          {t.retry}
        </Button>
      </div>
    );
  }

  // QL-493 — the summary card's labels, incl. the not-evaluable state.
  const summaryLabels = {
    year1Label: t.year1Label,
    coverageWarning: t.coverageWarning,
    breachMessage: t.breachMessage,
    notEvaluableNoRiskScore: t.notEvaluableNoRiskScore,
    notEvaluableNoSnapshot: t.notEvaluableNoSnapshot,
    notEvaluableMissingAssetData: t.notEvaluableMissingAssetData,
  };

  if (!state?.enabled) {
    // QL-493 — no forecast, but a ceiling applies: say so as the block it is,
    // not as an empty chart. Without a reason there is nothing to check.
    if (state?.notEvaluableReason) {
      return (
        <div className={hasValidationError ? "border border-destructive rounded-lg p-4" : ""}>
          <FlowComponentHeader title={config?.title} description={config?.description} />
          <ComplianceSummaryCard
            title={t.volatility}
            labels={summaryLabels}
            effective={null}
            notEvaluableReason={state.notEvaluableReason}
            year1Pct={null}
            ceilingPct={null}
            unknownPct={0}
            deviationAcceptanceEnabled={state.deviationAcceptanceEnabled}
          />
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noData}
      </div>
    );
  }

  const effective = state.effective;

  const componentStyle = config?.style ?? "none";
  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : componentStyle === "border"
      ? "border rounded-lg p-4"
      : componentStyle === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  return (
    <div className={wrapperClass}>
      <FlowComponentHeader title={config?.title} description={config?.description} />
      <div className="space-y-4 mt-2">
        {/* Horizon toggle — hidden when only one range is enabled */}
        {enabledHorizons.length > 1 && (
          <div className="flex justify-end">
            {/* A view switch over points the state already holds, never a
                persisted value — the same ToggleGroup the asset-class forecast
                chart uses (DESIGN 14j). */}
            <ToggleGroup
              type="single"
              value={horizon}
              onValueChange={(next) => {
                if (next) setHorizon(next as AssetClassForecastHorizon);
              }}
              className="h-9"
            >
              {enabledHorizons.map((h) => (
                <ToggleGroupItem key={h} value={h} className="h-7 px-3 text-xs">
                  {t[`horizon${h}` as const]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        {/* Vega-Lite volatility line chart */}
        <VolatilityLineChart
          points={visiblePoints}
          maxVolatilityPct={state.maxVolatilityPct}
          baselineVolatilityPct={state.baselineVolatilityPct}
          language={language}
        />

        {/* Legend strip: the line, the ceiling and (on a follow-up) the baseline,
            each with its figure — the same strip the equity share chart uses */}
        <ChartLegendStrip
          items={[
            ...(state.year1VolatilityPct != null
              ? [
                  {
                    key: "vol",
                    label: t.year1Label,
                    value: `${state.year1VolatilityPct.toFixed(1)}%`,
                    color: stripDark ? LINE_COLOR.dark : LINE_COLOR.light,
                    hint: year1Hint,
                    tone:
                      state.maxVolatilityPct != null &&
                      state.year1VolatilityPct > state.maxVolatilityPct
                        ? ("destructive" as const)
                        : ("default" as const),
                  },
                ]
              : []),
            ...(state.maxVolatilityPct != null
              ? [
                  {
                    key: "max",
                    label: t.ceilingLabel,
                    value: `${state.maxVolatilityPct.toFixed(1)}%`,
                    color: "#dc2626",
                    glyph: "line" as const,
                  },
                ]
              : []),
            ...(state.baselineVolatilityPct != null
              ? [
                  {
                    key: "baseline",
                    label: t.baselineLabel,
                    value: `${state.baselineVolatilityPct.toFixed(1)}%`,
                    color: stripDark ? BASELINE_COLOR.dark : BASELINE_COLOR.light,
                    glyph: "line" as const,
                    tone: "muted" as const,
                  },
                ]
              : []),
          ]}
        />

        {/* Data table (configurable) */}
        {config?.showTable && (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.date}</TableHead>
                <TableHead className="text-right">{t.volatility} (%)</TableHead>
                {state.maxVolatilityPct != null && (
                  <TableHead className="text-right">{t.ceilingLabel}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.baselineVolatilityPct != null && (
                <TableRow className="bg-muted/20">
                  <TableCell className="text-muted-foreground">{t.baselineLabel}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {state.baselineVolatilityPct.toFixed(2)}%
                  </TableCell>
                  {state.maxVolatilityPct != null && <TableCell />}
                </TableRow>
              )}
              {visiblePoints
                .filter((p) => p.kind !== "baseline" && p.kind !== "anniversary")
                .map((p) => {
                  const aboveCeiling =
                    state.maxVolatilityPct != null && p.volatilityPct > state.maxVolatilityPct;
                  const diff =
                    state.maxVolatilityPct != null
                      ? p.volatilityPct - state.maxVolatilityPct
                      : null;
                  return (
                    <TableRow key={p.label}>
                      <TableCell>{p.label}</TableCell>
                      <TableCell
                        className={`text-right tabular-nums ${aboveCeiling ? "text-destructive font-medium" : ""}`}
                      >
                        {p.volatilityPct.toFixed(2)}%
                      </TableCell>
                      {state.maxVolatilityPct != null && (
                        <TableCell
                          className={`text-right tabular-nums text-xs ${aboveCeiling ? "text-destructive" : "text-emerald-600"}`}
                        >
                          {aboveCeiling ? "✕" : "✓"}{" "}
                          {diff != null
                            ? diff >= 0
                              ? `+${diff.toFixed(1)}%`
                              : `${diff.toFixed(1)}%`
                            : ""}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}

        {/* Card 1: Summary — the shared card (QL-313), as the decision card is */}
        <ComplianceSummaryCard
          title={t.volatility}
          labels={summaryLabels}
          effective={effective}
          notEvaluableReason={state.notEvaluableReason}
          year1Pct={state.year1VolatilityPct}
          ceilingPct={state.maxVolatilityPct}
          ceilingChange={state.ceilingChangedSinceDecision}
          unknownPct={maxUnknownPct}
          deviationAcceptanceEnabled={state.deviationAcceptanceEnabled}
        />

        {/* Card 2: Deviation decision (only when breach + acceptance enabled) —
            the shared card (QL-313), so the two compliance charts cannot drift. */}
        {(effective === "deviation" || effective === "accepted" || effective === "not_accepted") &&
          state.deviationAcceptanceEnabled && (
            <DeviationDecisionCard
              idPrefix={`vc-${goalId}`}
              value={decision}
              onValueChange={setDecision}
              justification={justification}
              onJustificationChange={setJustification}
              hasStoredDecision={state.decision !== null}
              onSave={(value) => handleSave(value)}
              onClear={() => handleSave(null)}
              isSaving={saveMutation.isPending}
              disabled={disabled}
            />
          )}
      </div>
    </div>
  );
}
